import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';
import useTableInstance from '../../../../hooks/useTableInstance';
import { GoodsData, Stock } from '../../../../models/Goods';
import { SearchColumnFilter } from '../../../../blocks/table/TableFilterUtils';
import IconButton from '../../../../components/icon-button/IconButton';
import { CameraIcon } from '../../../../assets/icons/CameraIcon';

interface StockData {
  available: number;
  name: string;
  itemId: string;
  minMax: string;
  warehouseId: string;
  pictureLinks: string[];
}

export const useEmergencyStorageTable = (
  stocks: Stock[],
  filterWarehouseId: string | undefined,
  filterItemId: string | undefined,
  showDetails: (goodsData: GoodsData) => void
) => {
  const data = useMemo(() => {
    if (!stocks.length) return [];
    return stocks
      .filter((x) => {
        const warehouseMatch = !filterWarehouseId || x.warehouseId === filterWarehouseId;
        const productMatch = !filterItemId || x.product?.itemId === filterItemId;
        return warehouseMatch && productMatch;
      })
      .map((stock) => {
        const idSuffix = stock.product?.batchNumber || stock.product?.serialNumber;
        return {
          minMax: `${stock.minOnHand}/${stock.maxOnHand}`,
          name: stock.product?.name,
          itemId: stock.product?.itemId + (idSuffix ? ` (${idSuffix})` : ''),
          available: stock.available,
          warehouseId: stock.warehouseName ? `${stock.warehouseName} (${stock.warehouseId})` : stock.warehouseId,
          pictureLinks: stock.product?.pictureLinks
        } as StockData;
      });
  }, [stocks, filterWarehouseId, filterItemId]);

  const columns = useMemo(() => {
    const _columns = [
      {
        Header: 'Varenummer',
        accessor: 'itemId',
        width: 120,
        minWidth: 120,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Navn',
        accessor: 'name',
        width: 300,
        minWidth: 300,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Lager',
        accessor: 'warehouseId',
        width: 200,
        minWidth: 200,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Antal',
        accessor: 'available',
        width: 80,
        minWidth: 80,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Min/Max',
        accessor: 'minMax',
        width: 75,
        minWidth: 75,
        Filter: SearchColumnFilter,
        filter: 'text'
      }
    ] as Column<StockData>[];

    // Only show 'product-details' column if showDetails callback is set
    showDetails &&
      _columns.splice(0, 0, {
        Header: '',
        accessor: 'pictureLinks',
        width: 60,
        disableFilters: true,
        disableSortBy: true,
        Cell: (cellProps: CellProps<StockData>) => {
          const { cell } = cellProps;
          const data = cell.row.original;

          return (
            <IconButton
              variant="outlined"
              disabled={data.pictureLinks?.length === 0 || !data.pictureLinks}
              onClick={() => {
                showDetails({
                  goodsId: data.itemId,
                  goodsName: data.name,
                  pictureLinks: data.pictureLinks
                });
              }}
            >
              <CameraIcon size="20px" />
            </IconButton>
          );
        }
      });
    return _columns;
  }, [showDetails]);

  const tableInstance = useTableInstance<StockData>(data, columns);

  return { tableInstance };
};
